import { captureException } from '@sentry/browser'
import get from 'lodash/get'

import {
    fetchCurrencySettingsSuccess,
    fetchHiddenAutomationCapabilities,
    fetchHomeDashboardIdSuccess,
    fetchUserSurveyLastReadDateSuccess,
    mountAppFailure,
    mountAppRequest,
    mountAppSuccess,
} from 'actions/ui/app'
import { UserSettings } from 'api/Cerebro/unscoped/user'
import {
    fetchLatestActiveOrganizationIntegration,
    fetchLatestOrganizationAdIntegrations,
} from 'features/Auth/service/integrations'
import { fetchUserSettings } from 'features/Auth/service/userSettings'
import { userHasCustomerServicePermissions } from 'helpers/featurePermissions'
import {
    selectDomainValue as selectAuthDomainValue,
    selectOrgKey,
    selectUserOrganizationFeaturePermissions,
} from 'selectors/auth'

import {
    fetchAutomationCapabilities,
    fetchBrandGroups,
    fetchCustomEvents,
    fetchOrganizationLabels,
    fetchSovPermissions,
    fetchTriggeredAlerts,
    setDisplayCurrency,
} from './appData'
import { beginBootstrapContext } from './bootstrapContext'
import { dispatch, getReduxState } from './reduxBridge'

type ShouldCommit = () => boolean

function applySurveyLastRead(
    lastReadDate: UserSettings['feedbackSurveyLastRead'],
    shouldCommit: ShouldCommit
): void {
    try {
        if (shouldCommit()) {
            dispatch(fetchUserSurveyLastReadDateSuccess(lastReadDate))
        }
    } catch (error) {
        captureException(error)
    }
}

function applyHiddenAutomationCapabilities(
    hiddenAutomationCapabilities: UserSettings['hiddenAutomationCapabilities'],
    shouldCommit: ShouldCommit
): void {
    const userId = selectAuthDomainValue(getReduxState(), ['username'])
    if (!userId) {
        return
    }

    const featurePermissions =
        selectUserOrganizationFeaturePermissions(getReduxState())
    if (!userHasCustomerServicePermissions(featurePermissions)) {
        return
    }

    if (shouldCommit()) {
        dispatch(
            fetchHiddenAutomationCapabilities(hiddenAutomationCapabilities)
        )
    }
}

function applyCurrencySettings(
    currency: UserSettings['currency'],
    shouldCommit: ShouldCommit
): void {
    const userId = selectAuthDomainValue(getReduxState(), ['username'])
    if (!userId) {
        return
    }

    const orgKey = selectOrgKey(getReduxState())
    // `currency` maps orgKey → currency-code string at runtime, despite the
    // `CurrencyMap` value type; read it as such (parity with the saga's `any`).
    const currencyMap = currency as Record<string, string> | undefined
    const currencyCode = get(currencyMap, [orgKey], null)

    if (!shouldCommit()) {
        return
    }

    dispatch(fetchCurrencySettingsSuccess(currencyCode))
    if (currencyCode) {
        setDisplayCurrency(currencyCode)
    }
}

function applyHomeDashboardId(
    dashboard: UserSettings['homeDashboardId'],
    shouldCommit: ShouldCommit
): void {
    const orgKey = selectOrgKey(getReduxState())
    const dashboardId = get(dashboard, [orgKey], null)
    if (shouldCommit()) {
        dispatch(fetchHomeDashboardIdSuccess(dashboardId))
    }
}

async function applyAllSettings(shouldCommit: ShouldCommit): Promise<void> {
    const userSettings = await fetchUserSettings(shouldCommit)

    applySurveyLastRead(userSettings.feedbackSurveyLastRead, shouldCommit)
    applyHiddenAutomationCapabilities(
        userSettings.hiddenAutomationCapabilities,
        shouldCommit
    )
    applyCurrencySettings(userSettings.currency, shouldCommit)
    applyHomeDashboardId(userSettings.homeDashboardId, shouldCommit)
}

/**
 * Plain (non-saga) equivalent of `mountAppWorker`: sets the app-ready gate to
 * "mounting", loads global user settings and org-scoped data in parallel, then
 * settles the gate. Called at `AppLayout` mount and re-run on org/group switch.
 *
 * Every Redux commit is gated on `shouldCommit`, which defaults to a fresh
 * bootstrap-context guard. When an org/group switch supersedes this run (a
 * rapid second switch), the guard turns false and this run's stale writes —
 * including the `mountAppSuccess` gate — are dropped rather than clobbering the
 * latest context. Callers that own the transition (org-switch) pass their own
 * guard so their pre-bootstrap writes share the same context.
 */
export async function bootstrapApp(
    shouldCommit: ShouldCommit = beginBootstrapContext()
): Promise<void> {
    if (shouldCommit()) {
        dispatch(mountAppRequest())
    }

    try {
        await Promise.all([
            applyAllSettings(shouldCommit),
            fetchAutomationCapabilities(shouldCommit),
            fetchOrganizationLabels(shouldCommit),
            fetchBrandGroups(shouldCommit),
            fetchCustomEvents(shouldCommit),
            fetchLatestOrganizationAdIntegrations(shouldCommit),
            fetchLatestActiveOrganizationIntegration(shouldCommit),
            fetchTriggeredAlerts(shouldCommit),
            fetchSovPermissions(shouldCommit),
        ])

        if (shouldCommit()) {
            dispatch(mountAppSuccess())
        }
    } catch (error) {
        captureException(error)
        if (shouldCommit()) {
            dispatch(mountAppFailure(error))
        }
    }
}
